import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  BuildingOfficeIcon,
  ServerStackIcon,
  DevicePhoneMobileIcon,
  WifiIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  ClockIcon,
  TagIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import managementService from '../api/managementService';
import type { Customer, Asset, Device, Gateway } from '../context/ManagementContext';

interface EntityDetailsProps {
  type: 'customer' | 'asset' | 'device' | 'gateway';
}

const getEntityIcon = (type: string) => {
  switch (type) {
    case 'customer': return BuildingOfficeIcon;
    case 'asset': return ServerStackIcon;
    case 'device': return DevicePhoneMobileIcon;
    case 'gateway': return WifiIcon;
    default: return BuildingOfficeIcon;
  }
};

const getEntityColor = (type: string) => {
  switch (type) {
    case 'customer': return 'text-blue-600 bg-blue-50';
    case 'asset': return 'text-green-600 bg-green-50';
    case 'device': return 'text-purple-600 bg-purple-50';
    case 'gateway': return 'text-orange-600 bg-orange-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

export default function EntityDetails({ type }: EntityDetailsProps) {
  const { id } = useParams<{ id: string }>();

  const { data: entity, isLoading, error } = useQuery({
    queryKey: [type, id],
    queryFn: async () => {
      if (!id) throw new Error('No ID provided');
      
      switch (type) {
        case 'customer':
          return await managementService.customer.getById(id);
        case 'asset':
          return await managementService.asset.getById(id);
        case 'device':
          return await managementService.device.getById(id);
        case 'gateway':
          return await managementService.gateway.getById(id);
        default:
          throw new Error('Invalid entity type');
      }
    },
    enabled: !!id,
  });

  // Get children for hierarchical entities
  const { data: children = [] } = useQuery({
    queryKey: [type, id, 'children'],
    queryFn: async () => {
      if (!id) return [];
      
      switch (type) {
        case 'customer':
          return await managementService.customer.getChildren(id);
        case 'asset':
          // Get child assets
          const assets = await managementService.asset.getAll({ limit: 1000 });
          return assets.data.filter(a => a.parentId === id);
        default:
          return [];
      }
    },
    enabled: !!id && (type === 'customer' || type === 'asset'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading {type}</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>Unable to load {type} details. Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const IconComponent = getEntityIcon(type);
  const colorClass = getEntityColor(type);

  const renderEntitySpecificDetails = () => {
    switch (type) {
      case 'customer':
        const customer = entity as Customer;
        return (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.description || 'No description'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Parent Customer</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {customer.parent ? (
                    <Link to={`/management/customers/${customer.parent.id}`} className="text-blue-600 hover:text-blue-500">
                      {customer.parent.name}
                    </Link>
                  ) : (
                    'No parent'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Children</dt>
                <dd className="mt-1 text-sm text-gray-900">{children.length} child customers</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Deleted</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {customer.isDeleted ? 'Yes' : 'No'}
                </dd>
              </div>
            </div>
          </>
        );
      
      case 'asset':
        const asset = entity as Asset;
        return (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Floor Plan</dt>
                <dd className="mt-1 text-sm text-gray-900">{asset.floorPlan || 'No floor plan'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Additional Info</dt>
                <dd className="mt-1 text-sm text-gray-900">{asset.additional_info || 'No additional info'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Customer</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {asset.customer ? (
                    <Link to={`/management/customers/${asset.customer.id}`} className="text-blue-600 hover:text-blue-500">
                      {asset.customer.name}
                    </Link>
                  ) : (
                    'No customer assigned'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Parent Asset</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {asset.parent ? (
                    <Link to={`/management/assets/${asset.parent.id}`} className="text-blue-600 hover:text-blue-500">
                      {asset.parent.label}
                    </Link>
                  ) : (
                    'No parent'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Children</dt>
                <dd className="mt-1 text-sm text-gray-900">{children.length} child assets</dd>
              </div>
            </div>
          </>
        );
      
      case 'device':
        const device = entity as Device;
        return (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{device.description || 'No description'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Customer</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {device.customer ? (
                    <Link to={`/management/customers/${device.customer.id}`} className="text-blue-600 hover:text-blue-500">
                      {device.customer.name}
                    </Link>
                  ) : (
                    'No customer assigned'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Asset</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {device.asset ? (
                    <Link to={`/management/assets/${device.asset.id}`} className="text-blue-600 hover:text-blue-500">
                      {device.asset.label}
                    </Link>
                  ) : (
                    'No asset assigned'
                  )}
                </dd>
              </div>
            </div>
          </>
        );
      
      case 'gateway':
        const gateway = entity as Gateway;
        return (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Slave ID</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {gateway.id}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fetch Interval</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="inline-flex items-center">
                    <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                    {gateway.fetchIntervalMs ? (gateway.fetchIntervalMs / 1000) : 'N/A'} seconds
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">No description available</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Location</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="text-gray-500">No location</span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Customer</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {gateway.customer ? (
                    <Link to={`/management/customers/${gateway.customer.id}`} className="text-blue-600 hover:text-blue-500">
                      {gateway.customer.name}
                    </Link>
                  ) : (
                    'No customer assigned'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Asset</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {gateway.asset ? (
                    <Link to={`/management/assets/${gateway.asset.id}`} className="text-blue-600 hover:text-blue-500">
                      {gateway.asset.label}
                    </Link>
                  ) : (
                    'No asset assigned'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Operational Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    gateway.isPaused 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {gateway.isPaused ? (
                      <>
                        <PauseIcon className="h-3 w-3 mr-1" />
                        Paused
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-3 w-3 mr-1" />
                        Running
                      </>
                    )}
                  </span>
                </dd>
              </div>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/management/${type}s`}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to {type}s
          </Link>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/management/${type}s`}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
        </div>
      </div>

      {/* Entity Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className={`flex items-center justify-center h-16 w-16 rounded-lg ${colorClass} mr-4`}>
              <IconComponent className="h-8 w-8" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {type === 'asset' ? (entity as Asset).label : (entity as any).name}
              </h1>
              <div className="flex items-center mt-2 space-x-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <TagIcon className="h-3 w-3 mr-1" />
                  {entity.id}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  (entity as any).isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {(entity as any).isActive ? (
                    <>
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      Active
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-3 w-3 mr-1" />
                      Inactive
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Entity Details */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Details</h3>
          </div>
          <dl className="space-y-6">
            {/* Common fields */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(entity.createdAt).toLocaleDateString()} at {new Date(entity.createdAt).toLocaleTimeString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(entity.updatedAt).toLocaleDateString()} at {new Date(entity.updatedAt).toLocaleTimeString()}
                </dd>
              </div>
            </div>
            
            {/* Entity-specific fields */}
            {renderEntitySpecificDetails()}
          </dl>
        </div>
      </div>

      {/* Children (for hierarchical entities) */}
      {(type === 'customer' || type === 'asset') && children.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Child {type === 'customer' ? 'Customers' : 'Assets'} ({children.length})
            </h3>
            <div className="space-y-3">
              {children.slice(0, 10).map((child: any) => (
                <div key={child.id} className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <IconComponent className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {type === 'customer' ? child.name : child.label}
                      </div>
                      <div className="text-sm text-gray-500">{child.id}</div>
                    </div>
                  </div>
                  <Link
                    to={`/management/${type}s/${child.id}`}
                    className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                  >
                    View
                  </Link>
                </div>
              ))}
              {children.length > 10 && (
                <div className="text-center py-2">
                  <Link
                    to={`/management/${type}s`}
                    className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                  >
                    View all {children.length} children
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}